/**
 * The locale registry: which translation files exist, and what URL each is
 * served from.
 *
 * The files are source modules under `src/locales/`, imported for their URL
 * only (`?url`). Vite emits each one as a content-hashed asset —
 * `/assets/en-mwhBrJJU.json` — and inlines the resulting URL map here at build
 * time. Two properties follow, and both are the point of doing it this way:
 *
 *   - The fingerprint is derived from the file's bytes, so a translation edit
 *     is a different URL and therefore a guaranteed cache miss. Nothing has to
 *     be bumped by hand for a change to reach a browser.
 *   - Only the active language is downloaded (~3 kB gzipped). Importing the
 *     JSON itself instead of its URL would pull all 54 files, ~136 kB
 *     gzipped, into the bundle.
 *
 * See docs/adr/adr-001-translations-as-content-hashed-source-modules.md.
 */

const urlModules = import.meta.glob("../locales/*.json", {
  query: "?url",
  import: "default",
  eager: true,
});

export const FALLBACK_LANGUAGE = "en";

/** Language tag (as the file is named) -> URL of its translation file. */
export const localeUrls = Object.fromEntries(
  Object.entries(urlModules).map(([filePath, url]) => [
    filePath.slice(filePath.lastIndexOf("/") + 1, -".json".length),
    url,
  ])
);

/**
 * Tags are matched case-insensitively, so a lookup index is kept alongside the
 * exported map. `es-MX` is a real filename; `es-mx` is a plausible thing to be
 * handed.
 */
const canonicalTagByLowercase = new Map(
  Object.keys(localeUrls).map((tag) => [tag.toLowerCase(), tag])
);

/**
 * Tags a browser reports that have no file of their own, but an unambiguous
 * match among the script- or form-qualified ones.
 *
 * Chinese, Serbian and Norwegian ship only qualified files — `zh-Hans.json`
 * exists, `zh.json` does not — so neither an exact match nor the primary-subtag
 * fallback finds them. `navigator.language` on mainland-Chinese Chrome is
 * `zh-CN`, and without an entry here that resolves to English: a complete,
 * correct-looking UI in the wrong language, with nothing to signal it.
 *
 * A Map rather than an object literal, so a tag like "constructor" cannot match
 * an inherited property and be returned as though it were a language.
 */
const TAG_ALIASES = new Map([
  ["zh", "zh-Hans"], // bare Chinese conventionally means Simplified
  ["zh-cn", "zh-Hans"],
  ["zh-sg", "zh-Hans"],
  ["zh-mo", "zh-Hant"], // Macau reads Traditional, as zh-HK and zh-TW do
  ["sr", "sr-Cyrl"], // Serbian's default script is Cyrillic
  ["sr-rs", "sr-Cyrl"],
  ["no", "nb"], // the macrolanguage; Bokmal is the common written form
  ["fil", "tl"], // Filipino is standardised Tagalog
]);

/**
 * Resolve a language tag to the tag of the file that will actually serve it.
 *
 * Most files are keyed by primary subtag ("sv"), but some are region- or
 * script-qualified ("pt-BR", "es-MX", "zh-Hans", "sr-Cyrl"), so an exact match
 * is tried before falling back to the primary subtag. Stripping the region
 * unconditionally would send every Brazilian Portuguese reader to pt.json.
 *
 * Callers that register the loaded bundle with i18next need this rather than
 * the tag they asked with: a bundle whose contents came from pt-BR.json has to
 * be registered as "pt-BR", or every lookup against it misses.
 *
 * @param {string | undefined} tag e.g. "sv", "pt-BR", "en-GB"
 * @returns {string} a tag that always exists in the registry
 */
export function resolveLocaleTag(tag) {
  const wanted = (tag ?? "").toLowerCase();

  // Aliases sit after the exact match so a qualified file always wins for its
  // own tag, and before the primary-subtag fallback. They are deliberately not
  // consulted for the primary subtag: "zh-Hant-HK" would then alias via "zh"
  // to Simplified, which is a worse answer than English for a reader of
  // Traditional.
  return (
    canonicalTagByLowercase.get(wanted) ??
    TAG_ALIASES.get(wanted) ??
    canonicalTagByLowercase.get(wanted.split("-")[0]) ??
    FALLBACK_LANGUAGE
  );
}

/**
 * Resolve a language tag to the URL of the file that should serve it.
 *
 * @param {string | undefined} tag e.g. "sv", "pt-BR", "en-GB"
 * @returns {string} a URL that is always resolvable — English if nothing matches
 */
export function localeUrl(tag) {
  return localeUrls[resolveLocaleTag(tag)];
}
